// Funções puras de conexão (token de staff, backoff, URL do WebSocket).
// CommonJS (ver utils/messages.js).

const TOKEN_REFRESH_LEAD_MS = 2 * 60 * 1000
const TOKEN_MIN_REFRESH_DELAY_MS = 5 * 1000
const TOKEN_SAFETY_MARGIN_MS = 30 * 1000

const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 30 * 1000

const WS_DEFAULT_PATH = '/ws/chat'
const WS_CLOSE_INVALID_TICKET = 4001
const WS_CLOSE_REAUTH = 4009

/**
 * Instante (ms epoch) de expiração do token. Prefere `expiresIn` (relativo ao
 * recebimento — imune a relógio do navegador adiantado/atrasado); cai para
 * `expiresAt` (ISO) se `expiresIn` não vier.
 */
const resolveTokenExpiry = ({ expiresAt, expiresIn } = {}, now) => {
  const seconds = Number(expiresIn)
  if (Number.isFinite(seconds) && seconds > 0) return now + seconds * 1000
  const parsed = Date.parse(expiresAt)
  return Number.isFinite(parsed) ? parsed : now
}

// Atraso até a renovação proativa: `leadMs` antes de expirar, nunca menos que `minDelayMs`.
const computeRefreshDelay = (expiresAtMs, now, { leadMs = TOKEN_REFRESH_LEAD_MS, minDelayMs = TOKEN_MIN_REFRESH_DELAY_MS } = {}) => Math.max(minDelayMs, expiresAtMs - leadMs - now)

const isTokenUsable = (expiresAtMs, now, safetyMs = TOKEN_SAFETY_MARGIN_MS) => Number.isFinite(expiresAtMs) && expiresAtMs - now > safetyMs

/**
 * Backoff exponencial 1s → 30s com jitter de ±20% (nunca acima do teto).
 * `attempt` começa em 0.
 */
const computeBackoff = (attempt, { baseMs = BACKOFF_BASE_MS, maxMs = BACKOFF_MAX_MS, random = Math.random } = {}) => {
  const exponent = Math.max(0, Math.min(Number(attempt) || 0, 30))
  const capped = Math.min(maxMs, baseMs * (2 ** exponent))
  const jittered = capped * (0.8 + random() * 0.4)
  return Math.round(Math.min(maxMs, jittered))
}

const toWsProtocol = protocol => {
  if (protocol === 'https:') return 'wss:'
  if (protocol === 'http:') return 'ws:'
  return protocol
}

/**
 * URL do WebSocket com o ticket. Ordem de preferência:
 * 1. `wsUrl` do token de staff (absoluta, ou relativa ao host do apiBaseUrl);
 * 2. `ticketPath` devolvido por /staff/ws-ticket (ex.: '/ws/chat');
 * 3. `wss://<host do apiBaseUrl>/ws/chat`.
 * http→ws e https→wss.
 */
const buildWsUrl = ({
  wsUrl, apiBaseUrl, ticketPath, ticket,
}) => {
  const target = new URL(wsUrl || ticketPath || WS_DEFAULT_PATH, apiBaseUrl || undefined)
  target.protocol = toWsProtocol(target.protocol)
  target.searchParams.set('ticket', ticket)
  return target.toString()
}

module.exports = {
  TOKEN_REFRESH_LEAD_MS,
  TOKEN_MIN_REFRESH_DELAY_MS,
  TOKEN_SAFETY_MARGIN_MS,
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
  WS_DEFAULT_PATH,
  WS_CLOSE_INVALID_TICKET,
  WS_CLOSE_REAUTH,
  resolveTokenExpiry,
  computeRefreshDelay,
  isTokenUsable,
  computeBackoff,
  buildWsUrl,
}
